import { useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import StoreInput from "../../StoreComponents/StoreInput";
import AuthSolidButton from "./components/AuthSolidButton";
import { ReactComponent as AddUser } from "../../../assets/Add_User.svg";
import {
  formatPhone,
  getPhoneNumberMasking,
  validatePhone,
} from "../../../utils/StringUtils";
import { ButtonLoadingEllipsis } from "../../StoreComponents/StoreButton";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { UserLoginContext } from "./UserLoginProvider";
import { clearBrandEntity } from "../../../redux/reducers/brandEntity";
import { BrandclubTermsAndConditions } from "./TermsAndConditions";
import { ButtonBase, useTheme } from "@mui/material";
import { BRANDCLUB_BRAND_ID, getUserSignedInState } from "@brandclub/common-ui";

import OTPWrapper from "./OTPWrapper";
import { ReactComponent as RightArrow } from "../../../assets/right-arrow.svg";

import { NumberFormatCustom } from "@/components/StoreComponents/NumberFormatCustom";
import { BrandclubTextMessageVerbiage } from "./TextMessageVerbiage";

const ROW_SPACING = 20;

const SignIn = ({
  isUpdatePhoneFlow = false,
}: {
  isUpdatePhoneFlow?: boolean;
}) => {
  const { send, signIn, signInError, setSignInError, submittingSignIn } =
    useContext(UserLoginContext);
  const appConfig = useAppSelector(({ appConfig }) => appConfig);
  const [searchParams] = useSearchParams();

  const dispatch = useAppDispatch();

  // Parse OAuth redirect path from URL state parameter
  const oauthRedirectPath = (() => {
    try {
      const stateParam = searchParams.get('state');
      console.log('[OAuth SignIn] state param:', stateParam);
      if (stateParam) {
        const decoded = JSON.parse(decodeURIComponent(stateParam));
        console.log('[OAuth SignIn] decoded state:', decoded);
        console.log('[OAuth SignIn] redirectPath:', decoded.redirectPath);
        return decoded.redirectPath;
      }
    } catch (e) {
      console.log('[OAuth SignIn] Failed to parse state:', e);
      // Not OAuth flow or invalid state
    }
    return null;
  })();

  useEffect(() => {
    if (appConfig?.domainConfig.brandId === BRANDCLUB_BRAND_ID) {
      dispatch(clearBrandEntity());
    }
  }, [dispatch, appConfig?.domainConfig.brandId]);

  // Handle OAuth redirect for already-authenticated users
  useEffect(() => {
    if (oauthRedirectPath) {
      getUserSignedInState().then(({ signedIn }) => {
        if (signedIn) {
          console.log('[OAuth SignIn] User already authenticated, redirecting to:', oauthRedirectPath);
          window.location.href = oauthRedirectPath;
        }
      });
    }
  }, [oauthRedirectPath]);

  const [phone, setPhone] = useState("");

  const theme = useTheme();

  const submit = async (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e?.preventDefault();
    const phoneNumber = formatPhone(phone, "e164");

    // If OAuth flow, pass redirectPath through to the machine
    if (oauthRedirectPath) {
      send({ type: "SIGN_IN", redirectPath: oauthRedirectPath } as any);
    }

    signIn(
      phoneNumber,
      isUpdatePhoneFlow,
      (isNewUser: boolean, session: any) => {
        const payload = {
          type: "MOVE_TO_VERIFY_OTP",
          cognitoSession: session,
          otpPhoneNumber: phoneNumber,
          isNewUser,
          ...(oauthRedirectPath ? { redirectPath: oauthRedirectPath } : {}),
        };
        console.log('[OAuth SignIn] Sending MOVE_TO_VERIFY_OTP with payload:', payload);
        return send(payload);
      }
    );
  };

  const clearErrors = () => {
    setSignInError("");
  };

  const isNumberValid = validatePhone(phone);

  const phoneMasking = getPhoneNumberMasking(phone || "");

  return (
    <OTPWrapper>
      <div className="auth_container">
        <AddUser
          style={{
            width: 75,
            height: 75,
            fill: theme.vars.palette.text.primary,
          }}
        />
        <div className="page_title">Enter your phone number</div>
        <div className="page_subtitle">
          We will text you a verification code.
        </div>
        <StoreInput
          errorMsg={signInError || ""}
          value={phone}
          autoFocus
          onChange={(e) => {
            const value = e.target.value;
            setPhone(value?.trim());
            clearErrors();
          }}
          slotProps={{
            input: {
              inputComponent: NumberFormatCustom as any,
              inputProps: {
                format: phoneMasking,
              },
              disableUnderline: true,
            },
          }}
          placeholder="Phone number"
          style={{ marginTop: ROW_SPACING }}
        ></StoreInput>
        <AuthSolidButton
          type="submit"
          onClick={submit}
          disabled={submittingSignIn || !isNumberValid}
          style={{ marginTop: ROW_SPACING }}
          sx={{
            [theme.breakpoints.down("sm")]: {
              maxWidth: "100%",
            },
          }}
        >
          {submittingSignIn ? (
            <>
              Submitting
              <ButtonLoadingEllipsis
                blue
                baseFontSize={16}
                customColor="#dcdddc"
              />
            </>
          ) : (
            "Continue"
          )}
        </AuthSolidButton>
        {isUpdatePhoneFlow ? null : (
          <ButtonBase
            onClick={() => send({ type: "CHANGE_PHONE_NUMBER" })}
            sx={{
              marginTop: "30.5px",
              fontWeight: 500,
              display: "flex",
              fontSize: "14px",
              lineHeight: "22px",
              flexDirection: "row",
              alignItems: "center",
              gap: "6.5px",
            }}
          >
            Update your phone number <RightArrow />
          </ButtonBase>
        )}
        <div className="termsAgreement" style={{ maxWidth: 300 }}>
          <BrandclubTermsAndConditions />
        </div>
        <div className="textMessageVerbiage">
          <BrandclubTextMessageVerbiage />
        </div>
      </div>
    </OTPWrapper>
  );
};

export default SignIn;
